/**
 * Utility functions for handling authentication redirects across platforms
 */

import { isIOSNativeApp, isAndroidNativeApp } from './platformUtils';

/**
 * Get the appropriate email redirect URL for email verification
 * @returns The web bridge redirect URL for all platforms
 */
export function getEmailRedirectUrl(): string {
  // Always use web bridge page for email redirects across all platforms
  // This page will extract session tokens and forward to native app if installed
  // Solves the limitation where custom URL schemes cannot receive session tokens
  return 'https://thematmonster.com/auth/email-callback';
}

/**
 * Get the appropriate deep link URL for the app
 * @param path - The path within the app (default: '')
 * @param params - Query parameters to include
 * @returns The appropriate deep link URL
 */
export function getAppDeepLink(path: string = '', params?: Record<string, string>): string {
  const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
  
  // For native apps, use custom URL scheme
  if (isIOSNativeApp() || isAndroidNativeApp()) {
    return `thematmonster://${path}${queryString}`;
  }
  
  // For web, use regular URL
  return `${window.location.origin}/${path}${queryString}`;
}

/**
 * Check if a URL is a deep link for this app
 * @param url - The URL to check
 * @returns True if the URL is a deep link for this app
 */
export function isAppDeepLink(url: string): boolean {
  return url.startsWith('thematmonster://') || url.startsWith('thematmonster-auth://');
}

/**
 * Parse a deep link URL to extract the path and parameters
 * @param url - The deep link URL to parse
 * @returns Object containing the path and parameters
 */
export function parseDeepLink(url: string): { path: string; params: Record<string, string> } {
  try {
    const urlObj = new URL(url);
    const path = urlObj.hostname + urlObj.pathname;
    const params: Record<string, string> = {};

    // Extract query parameters
    urlObj.searchParams.forEach((value, key) => {
      params[key] = value;
    });

    // Also extract hash parameters if present (for auth tokens)
    if (urlObj.hash) {
      const hashParams = new URLSearchParams(urlObj.hash.substring(1));
      hashParams.forEach((value, key) => {
        params[key] = value;
      });
    }

    return { path, params };
  } catch (error) {
    console.error('Error parsing deep link:', error);
    return { path: '', params: {} };
  }
}
